import bizAiService from '@/modules/bizai/services/BizAiService';
import smartReplyStore from '@/modules/bizai/store/smartReplyStore';

const state = {
  visible: false,
  launched: false,
  staffAiSettings: {
    isLoaded: false,
    aiRecommendations: {
      enabled: undefined,
      subOptions: {
        nextBestAction: undefined,
        estimate: undefined,
        scheduling: undefined,
      },
    },
    smartReply: {
      enabled: undefined,
    },
  },
  recommendations: [],
};

const createClientActionsMap = (recommendations) => {
  const clientActionsMap = {};

  if (!recommendations) return clientActionsMap;

  // Process each recommendation
  recommendations.forEach((recommendation) => {
    // Check if the recommendation has a context with a client UID
    if (recommendation.context && recommendation.context.context_uid) {
      const clientUid = recommendation.context.context_uid;

      // Only include clients that have actions
      if (recommendation.actions && recommendation.actions.length > 0) {
        // Initialize the client entry if it doesn't exist
        if (!clientActionsMap[clientUid]) {
          clientActionsMap[clientUid] = recommendation.actions;
          clientActionsMap[clientUid].title = recommendation.display?.title;
        } else {
          clientActionsMap[clientUid] = [
            ...clientActionsMap[clientUid],
            ...recommendation.actions,
          ];
          // Ensure title is preserved
          clientActionsMap[clientUid].title = clientActionsMap[clientUid].title || recommendation.display?.title;
        }
      }
    }
  });

  return clientActionsMap;
};

const getters = {
  isVisible: (state) => state.visible,
  isLaunched: (state) => state.launched,
  isAiRecommendationsEnabled: (state) => state.staffAiSettings.aiRecommendations.enabled,
  isNextBestAction: (state) => state.staffAiSettings.aiRecommendations.subOptions.nextBestAction,
  isEstimation: (state) => state.staffAiSettings.aiRecommendations.subOptions.estimate,
  isScheduling: (state) => state.staffAiSettings.aiRecommendations.subOptions.scheduling,
  isSmartReplyEnabled: (state) => state.staffAiSettings.smartReply.enabled,
  recommendations: (state) => state.recommendations,
  recommendation: (state) => state.recommendations[0],
  clientRecommendations: (state) => createClientActionsMap(state.recommendations),
  getRecommendationByMatter: (state) => (matterUid) => state.recommendations?.find(
    (recommendation) => recommendation.context.context_uid === matterUid,
  ),
};

const mutations = {
  setVisibility: (state, visible) => {
    state.launched = true;
    state.visible = Boolean(visible);
  },
  toggleVisibility: (state) => {
    state.launched = true;
    state.visible = !state.visible;
  },
  setLaunched: (state, launched) => {
    state.launched = Boolean(launched);
  },
  setStaffAiSettingsLoaded: (state, staffAiSettingsLoaded) => {
    state.staffAiSettings.isLoaded = Boolean(staffAiSettingsLoaded);
  },
  setAiRecommendationEnabled: (state, aiRecommendationEnabled) => {
    state.staffAiSettings.aiRecommendations.enabled = Boolean(aiRecommendationEnabled);
  },
  setNextBestActionEnabled: (state, nextBestAction) => {
    state.staffAiSettings.aiRecommendations.subOptions.nextBestAction = Boolean(nextBestAction);
  },
  setEstimationEnabled: (state, estimation) => {
    state.staffAiSettings.aiRecommendations.subOptions.estimate = Boolean(estimation);
  },
  setSchedulingEnabled: (state, scheduling) => {
    state.staffAiSettings.aiRecommendations.subOptions.scheduling = Boolean(scheduling);
  },
  setSmartReplyEnabled: (state, smartReplyEnabled) => {
    state.staffAiSettings.smartReply.enabled = Boolean(smartReplyEnabled);
  },
  setRecommendations(state, value) {
    state.recommendations = value;
  },
};

const actions = {
  setVisibility: ({ commit }, visible) => {
    commit('setVisibility', visible);
  },
  toggleVisibility: ({ commit }) => {
    commit('toggleVisibility');
  },
  setLaunched: ({ commit }, launched) => {
    commit('setLaunched', launched);
  },
  async fetchStaffAiSettings({ commit, rootGetters, state }) {
    const staffUid = rootGetters['AuthStore/staffUid'];
    if (!staffUid) {
      return;
    }
    if (!state.staffAiSettings.isLoaded) {
      const res = await bizAiService.getStaffAiSettings(staffUid);
      if (res.success) {
        const staffAiSettings = res.data;
        commit('setStaffAiSettingsLoaded', true);
        commit('setAiRecommendationEnabled', staffAiSettings.ai_recommendations.enable);
        commit('setNextBestActionEnabled', staffAiSettings.ai_recommendations.sub_options.next_best_action);
        commit('setEstimationEnabled', staffAiSettings.ai_recommendations.sub_options.estimate);
        commit('setSchedulingEnabled', staffAiSettings.ai_recommendations.sub_options.scheduling);
        commit('setSmartReplyEnabled', staffAiSettings.smart_reply?.enable);
      } else {
        throw new Error('Failed to fetch staff ai settings');
      }
    }
  },
  async updateAllSettings({ commit, rootGetters, state }, settings = {}) {
    if (state.staffAiSettings.isLoaded) {
      const currentSettings = state.staffAiSettings.aiRecommendations;
      const updatedSettings = {
        enable: 'enabled' in settings ? settings.enabled : currentSettings.enabled,
        sub_options: {
          next_best_action: 'nextBestAction' in settings ? settings.nextBestAction : currentSettings.subOptions.nextBestAction,
          estimate: 'estimate' in settings ? settings.estimate : currentSettings.subOptions.estimate,
          scheduling: 'scheduling' in settings ? settings.scheduling : currentSettings.subOptions.scheduling,
        },
      };

      const res = await bizAiService.updateStaffAiSettings(
        rootGetters['AuthStore/staffUid'],
        {
          staff_uid: rootGetters['AuthStore/staffUid'],
          ai_recommendations: updatedSettings,
        },
      );

      if (res.success) {
        const staffAiSettings = res.data;
        commit('setAiRecommendationEnabled', staffAiSettings.ai_recommendations.enable);
        commit('setNextBestActionEnabled', staffAiSettings.ai_recommendations.sub_options.next_best_action);
        commit('setEstimationEnabled', staffAiSettings.ai_recommendations.sub_options.estimate);
        commit('setSchedulingEnabled', staffAiSettings.ai_recommendations.sub_options.scheduling);
      } else {
        throw new Error('Failed to update staff ai settings');
      }
    } else {
      throw new Error('Failed to update staff ai settings');
    }
  },
  async updateSmartReplySettings({ commit, rootGetters }, enabled) {
    const res = await bizAiService.updateStaffAiSettings(
      rootGetters['AuthStore/staffUid'],
      {
        staff_uid: rootGetters['AuthStore/staffUid'],
        smart_reply: {
          enable: enabled,
        },
      },
    );

    if (res.success) {
      commit('setSmartReplyEnabled', res.data.smart_reply?.enable);
    } else {
      throw new Error('Failed to update staff ai settings');
    }
  },
  async updateRecommendation({ commit }, { uid, dismissed = false }) {
    if (dismissed) {
      try {
        await bizAiService.updateRecommendation(uid, dismissed);
      } catch (error) {
        console.error('Failed to update recommendation', error);
      }
    }
    const newRecommendations = state.recommendations.filter((rec) => rec.uid !== uid);
    commit('setRecommendations', newRecommendations);
  },
  async fetchRecommendations({ commit }, params) {
    try {
      const recommendations = await bizAiService.getRecommendations(params);
      commit('setRecommendations', recommendations);
    } catch (error) {
      console.error('Failed to fetch recommendations', error);
    }
  },
};

const modules = {
  SmartReplyStore: smartReplyStore,
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules,
};
